import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { getClaimData } from '../../../../../../store/claim/action-creators'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'

interface IFormData {
  work_responsible?: number
  date_work_completion?: string
  time_work_completion?: string
  comment?: string
}

interface WorksModalProps {
  id: number | string
  isModalOpen: boolean
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
}

export const WorksModal: React.FC<WorksModalProps> = memo((props: WorksModalProps) => {
  const dispatch = useAppDispatch()
  const { id, isModalOpen, onCancel } = props
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [enums, setEnums] = useState<any>({})
  const [formData, setFormData] = useState<IFormData>({
    work_responsible: undefined,
    date_work_completion: undefined,
    time_work_completion: undefined,
    comment: undefined,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isErrorMode, setIsErrorMode] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const resetState = useCallback(() => {
    setLoadingError(null)
    setSaveError(null)
    setEnums({})
    setFormData({
      work_responsible: undefined,
      date_work_completion: undefined,
      time_work_completion: undefined,
      comment: undefined,
    })
    setIsLoading(false)
    setIsErrorMode(false)
    setIsSuccess(false)
  }, [])

  const extractErrorMessage = (error: unknown): string | undefined => {
    if (error instanceof Error) {
      return error.message
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message)
    }
    if (typeof error === 'string') {
      return error
    }
    return undefined
  }

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetchAPI(`/api/claim/${id}/work`)
      if (!res.enums) {
        throw new Error('No enums in response')
      }
      setEnums(res.enums)
      setLoadingError(null)
      setIsErrorMode(false)
    } catch (error: unknown) {
      console.error('fetch error', { error, type: typeof error })
      const errorMessage = extractErrorMessage(error)
      setLoadingError(
        errorMessage === 'Failed to fetch'
          ? 'Нет соединения с сервером, попробуйте позже'
          : 'Ошибка при загрузке данных',
      )
      setIsErrorMode(true)
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isModalOpen) {
      fetchData()
    } else {
      resetState()
    }
  }, [isModalOpen, fetchData, resetState])

  const handleOk = useCallback(async () => {
    if (isErrorMode) {
      await fetchData()
      return
    }
    setSaveError(null)
    const isRequiredFields = isFormDataRequired(formData, actualSchema)
    if (!isRequiredFields) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните все обязательные поля',
      })
      return
    }
    setIsLoading(true)
    try {
      await fetchAPI(`/api/claim/${id}/add-work`, {
        method: 'POST',
        body: JSON.stringify(formData),
      })
      setIsSuccess(true)
      setTimeout(() => {
        onCancel?.({} as React.MouseEvent<HTMLElement>)
      }, 1500)
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error)
      setSaveError(
        errorMessage === 'Failed to fetch'
          ? 'Нет соединения с сервером, попробуйте позже'
          : 'Не удалось создать задачу, попробуйте позже',
      )
    } finally {
      setIsLoading(false)
      await dispatch(getClaimData(id))
    }
  }, [formData, isErrorMode, fetchData, onCancel])

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    if (!isLoading) {
      resetState()
      onCancel?.(e)
    }
  }

  const onChange = useCallback(data => {
    setFormData(prevData => ({
      ...prevData,
      ...data.formData,
    }))
  }, [])

  interface ISchemaActual {
    resultObject: {}
    resultSchema: {
      properties: {
        work_responsible: any
        time_work_completion: any
        comment: any
      }
      required: string[]
    }
  }

  const actualSchema = useMemo(() => {
    const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual
    if (schemaActual.resultSchema && schemaActual.resultSchema.properties && enums) {
      if (enums.responsible) {
        schemaActual.resultSchema.properties.work_responsible.oneOf = enums.responsible.map(
          (manager: any) => ({ const: manager.id, title: manager.value }),
        )
        schemaActual.resultSchema.properties.work_responsible.enum =
          schemaActual.resultSchema.properties.work_responsible.oneOf.reduce((acc, curr) => {
            acc.push(curr.const)
            return acc
          }, [])
      }
      schemaActual.resultSchema.properties.time_work_completion.oneOf = [
        {
          const: '09:00 - 11:00',
          title: '09:00 - 11:00',
        },
        {
          const: '11:00 - 13:00',
          title: '11:00 - 13:00',
        },
        {
          const: '13:00 - 15:00',
          title: '13:00 - 15:00',
        },
        {
          const: '15:00 - 17:00',
          title: '15:00 - 17:00',
        },
        {
          const: '17:00 - 19:00',
          title: '17:00 - 19:00',
        },
      ]
      schemaActual.resultSchema.properties.time_work_completion.enum =
        schemaActual.resultSchema.properties.time_work_completion.oneOf.reduce((acc, curr) => {
          acc.push(curr.const)
          return acc
        }, [])
    }
    return schemaActual.resultSchema || {}
  }, [schema, formData, enums])

  if (!isModalOpen) {
    return null
  }

  const okTitle = isErrorMode ? 'Повторить' : 'Назначить'
  const cancelTitle = isErrorMode ? 'Закрыть' : 'Отмена'

  return (
    <Modal
      title={schema.title}
      onOk={handleOk}
      onCancel={handleCancel}
      open={isModalOpen}
      destroyOnHidden={true}
      closable={!isLoading}
      okButtonProps={{ disabled: isLoading }}
      okText={okTitle}
      cancelButtonProps={{ disabled: isLoading }}
      cancelText={cancelTitle}
      maskClosable={!isLoading}
    >
      <Spin spinning={isLoading}>
        {isErrorMode ? (
          <Alert type="error" message={loadingError} />
        ) : (
          <DefaultJsonForm
            formData={formData}
            schema={actualSchema}
            onChange={onChange}
            orientation="horizontal"
          />
        )}
      </Spin>
      {!isErrorMode && saveError && <Alert type="error" message={saveError} />}
      {isSuccess && <Alert type="success" message={'Карточка работ успешно создана'} />}
    </Modal>
  )
})
